/**
 * Elegant SVG placeholders for the two client assets that have not yet arrived
 * (logo MABS + professional portrait of Maître Sanou).
 *
 * Each component accepts an optional `src` prop — when the real image is
 * uploaded through the admin, simply passing the URL replaces the placeholder
 * with no other code change.
 */
import { Scale, Camera } from "lucide-react";

export function LogoMark({ size = "md", src, className = "" }) {
    const dims =
        size === "sm"
            ? { box: "h-10", mono: "text-xl", icon: 18, sub: false }
            : size === "lg"
              ? { box: "h-24", mono: "text-5xl", icon: 42, sub: true }
              : { box: "h-14", mono: "text-2xl", icon: 24, sub: false };
    if (src) {
        return (
            <img
                src={src}
                alt="Cabinet d'avocats Maître Mina Adja Bintou Sanou"
                className={`${dims.box} w-auto object-contain ${className}`}
                data-testid="logo-mark"
            />
        );
    }
    return (
        <div
            className={`flex items-center gap-3 ${dims.box} ${className}`}
            data-testid="logo-mark"
        >
            <div className="relative flex items-center justify-center w-10 h-10 border border-gold/70 rounded-sm bg-ink">
                <Scale
                    size={dims.icon}
                    className="text-gold"
                    strokeWidth={1.4}
                />
            </div>
            <div className="leading-none">
                <div
                    className={`font-serif ${dims.mono} tracking-[0.18em] text-gold`}
                >
                    CMABS
                </div>
                {dims.sub && (
                    <div className="mt-2 text-[0.62rem] tracking-[0.32em] text-neutral-400 uppercase">
                        Cabinet d'avocats
                    </div>
                )}
            </div>
        </div>
    );
}

export function PortraitPlaceholder({ src, alt, className = "" }) {
    if (src) {
        return (
            <img
                src={src}
                alt={alt || "Maître Mina Adja Bintou Sanou"}
                className={className}
                data-testid="portrait-image"
            />
        );
    }
    // Elegant placeholder — dark card, gold rim, subtle silhouette shape.
    return (
        <div
            data-testid="portrait-placeholder"
            className={`relative overflow-hidden bg-ink-surface border border-ink-borderStrong ${className}`}
            role="img"
            aria-label={alt || "Portrait de Maître Sanou (à venir)"}
        >
            <svg
                viewBox="0 0 400 560"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="xMidYMid slice"
                className="absolute inset-0 w-full h-full"
            >
                <defs>
                    <linearGradient id="pg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#151515" />
                        <stop offset="100%" stopColor="#050505" />
                    </linearGradient>
                    <radialGradient id="pg2" cx="50%" cy="35%" r="55%">
                        <stop
                            offset="0%"
                            stopColor="#D4AF37"
                            stopOpacity="0.15"
                        />
                        <stop
                            offset="100%"
                            stopColor="#D4AF37"
                            stopOpacity="0"
                        />
                    </radialGradient>
                </defs>
                <rect width="400" height="560" fill="url(#pg)" />
                <rect width="400" height="560" fill="url(#pg2)" />
                {/* Stylised silhouette — abstract, deliberately non-representational. */}
                <g fill="none" stroke="#D4AF37" strokeWidth="0.6" opacity="0.4">
                    <circle cx="200" cy="180" r="72" />
                    <path d="M 96 560 C 96 380, 304 380, 304 560 Z" />
                </g>
                <g stroke="#D4AF37" opacity="0.9">
                    <line x1="40" y1="40" x2="90" y2="40" strokeWidth="1" />
                    <line x1="40" y1="40" x2="40" y2="90" strokeWidth="1" />
                    <line x1="360" y1="520" x2="310" y2="520" strokeWidth="1" />
                    <line x1="360" y1="520" x2="360" y2="470" strokeWidth="1" />
                </g>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                <div className="w-12 h-12 border border-gold/70 rounded-full flex items-center justify-center mb-4 bg-ink/70">
                    <Camera
                        size={20}
                        strokeWidth={1.4}
                        className="text-gold"
                    />
                </div>
                <div className="font-serif text-2xl text-gold tracking-wide">
                    Maître Sanou
                </div>
                <div className="mt-2 text-[0.65rem] tracking-[0.28em] uppercase text-neutral-400">
                    Portrait officiel
                </div>
            </div>
        </div>
    );
}
